import type { ComputedRef, Ref } from 'vue'
import { ref, watch } from 'vue'
import { getRequestAnimationFrame, getTimeout } from '@/helpers/globals'
import { useSwipe } from '@vueuse/core'
import type { RefOrComputedRef } from '@/types/utils'

// allows vertical scrolling without accidentally triggering a swipe
const horizontalThreshold = -50

/**
 * @param menuRef   Listen to for swipe events on this element
 *
 * @param onBack    Will be called when the user swipes back.
 *                  Must change the data in a way that triggers a transition,
 *                  i.e. hides an item and optionally shows the previous one
 *
 * @param onRevert  Will be called when the user cancels the swipe by
 *                  releasing the touch below the threshold.
 *                  Must revert the data to original state
 *
 * @param shouldTrigger     Swipes will be ignored unless true.
 *                          Used to disable HeaderMenu swipes when there are swipable children
 *
 * @param duration  Duration of the transition in ms.
 *                  Must coincide with the CSS transition duration
 */
export function useHeaderMenuTransitionHooks(
    menuRef: RefOrComputedRef<HTMLElement | null>,
    onBack: () => void,
    onRevert: () => void,
    shouldTrigger: ComputedRef<boolean> | Ref<boolean>,
    duration = 175
): {
    backProgrammatic: () => void
    onBeforeEnter: (el: Element) => void
    onEnter: (e: Element, done: () => void) => void
    onAfterEnter: (el: Element) => void
    onLeave: (e: Element, done: () => void) => void
} {
    const { setTimeout } = getTimeout() ?? { setTimeout: undefined }

    let isTransitioning = false

    const { lengthX, isSwiping } = useSwipe(menuRef, {
        threshold: 0,
        onSwipe(e) {
            // in case a parent also uses this composable
            if (isTransitioning) {
                e.stopPropagation()
            }
        },
    })

    // controls transition direction
    const isReverse = ref(false)

    const back = () => {
        isReverse.value = true
        isTransitioning = true

        onBack()
    }

    const isBackProgrammatic = ref(false)
    const backProgrammatic = () => {
        isBackProgrammatic.value = true

        back()
    }

    // start animation when the user swipes back
    watch(lengthX, (value, oldValue) => {
        // this will prevent other elements from animating if
        // they are triggered by swipes on the same ref
        if (isTransitioning) {
            return
        }

        if (!shouldTrigger.value) {
            return
        }

        // do it only once when going past the threshold
        if (oldValue > horizontalThreshold && value <= horizontalThreshold) {
            back()
        }
    })

    // will be called when the swipe is below the
    // treshold, effectively cancelling the transition
    const revert = () => {
        isReverse.value = false
        isCancelled.value = true
        previousX = 0

        onRevert()
    }

    function onBeforeEnter(el: Element) {
        if (isReverse.value) {
            el.classList.add('slide-reverse-enter-from')
        } else {
            el.classList.add('slide-enter-from')
        }
    }

    const requestAnimationFrame = getRequestAnimationFrame()

    // the transition is cancelled if swipe is below certain treshold
    const isCancelled = ref(false)

    // called one frame after the element is inserted.
    // use this to start the entering animation.
    function onEnter(e: Element, done: () => void) {
        const el = e as HTMLElement

        // transition after treshold
        // el.style.transition = `transform ${30}ms`

        if (isReverse.value) {
            // if the user is still swiping, keep the transitioned element
            // attached to their finger
            // else, use the default transition
            const animate = () => {
                if (isSwiping.value) {
                    const lengthXValue = lengthX.value + horizontalThreshold
                    const width = el.clientWidth
                    const progress = Math.abs(lengthXValue / width)

                    const translate = -lengthXValue - width

                    const translateX = translate + width * (1 - progress) * 0.8

                    el.style.transform = `translateX(${translateX}px)`

                    requestAnimationFrame(animate)
                } else {
                    isBackProgrammatic.value = false
                    el.style.transform = ''
                    el.classList.add('slide-reverse-enter-active')

                    setTimeout?.(() => {
                        el.classList.remove('slide-reverse-enter-from')

                        setTimeout?.(done, duration)
                    })
                }
            }

            requestAnimationFrame(animate)
        } else {
            if (isCancelled.value) {
                isCancelled.value = false

                el.style.transform = `translateX(${Math.max(
                    -lengthX.value + horizontalThreshold,
                    0
                )}px)`
            }

            el.classList.add('slide-enter-active')

            setTimeout?.(() => {
                el.classList.remove('slide-enter-from')
                el.style.transform = ''

                setTimeout?.(done, duration)
            })
        }
    }

    // called when the enter transition has finished.
    function onAfterEnter(el: Element) {
        isReverse.value = false
        isTransitioning = false
        el.classList.remove('slide-enter-active')
        el.classList.remove('slide-reverse-enter-active')
    }

    // tracks swipe back speed
    let previousX = 0

    // called when the leave transition starts.
    // use this to start the leaving animation.
    const onLeave = (e: Element, done: () => void) => {
        const el = e as HTMLElement

        if (isReverse.value) {
            const animate = () => {
                // if the user is still swiping, keep the transitioned element
                // attached to their finger
                // else, use the default transition
                if (isSwiping.value) {
                    previousX = lengthX.value

                    el.style.transform = `translateX(${Math.max(
                        -lengthX.value + horizontalThreshold,
                        0
                    )}px)`
                    el.style.zIndex = '1'

                    // prevent scrolling of menu items while swiping
                    el.style.overflow = 'hidden'

                    requestAnimationFrame(animate)
                } else {
                    if (
                        !isBackProgrammatic.value &&
                        Math.abs(
                            (lengthX.value - horizontalThreshold) /
                                el.clientWidth
                        ) < 0.55 &&
                        previousX - lengthX.value < 5
                    ) {
                        revert()
                    } else {
                        el.style.transform = ''
                        el.classList.add('slide-reverse-leave-active')
                        el.classList.add('slide-reverse-leave-to')

                        setTimeout?.(done, duration)
                    }
                }
            }

            requestAnimationFrame(animate)
        } else {
            if (isCancelled.value) {
                // transition to -20% which is the original enter position
                // when swiping back
                el.classList.add('slide-cancelled-leave-active')
                el.classList.add('slide-cancelled-leave-to')
            } else {
                // does a full transition for backProgrammatic()
                el.classList.add('slide-leave-active')
                el.classList.add('slide-leave-to')
            }

            setTimeout?.(done, duration)
        }
    }

    return {
        backProgrammatic,
        onBeforeEnter,
        onEnter,
        onAfterEnter,
        onLeave,
    }
}
